import * as React from 'react';
import classnames from 'classnames';

import { FormField } from 'components/form';
import InputArray from 'components/inputs/input-array/InputArray';
import CharacterCount from 'components/character-count/CharacterCount';
import Tooltip from 'components/Tooltip';

import InsightsReportsQueriesInputField from 'modules/insights/reports/components/queries/inputs/field/InsightsReportsQueriesInputField';
import InsightsReportsQueriesModalStepDetailsKpi from './kpis/InsightsReportsQueriesModalStepDetailsKpi';

import { __t, applyTranslations } from 'utils/language';
import {
  sanitizeHashtagValue
} from 'modules/insights/reports/utils/insights-reports';

import {
  HASHTAG,
  HASHTAGS,
  IMPRESSIONS,
  KPI_DEFAULTS,
  MAX_NUM_ITEMS,
  REACH,
  TITLE
} from 'modules/insights/reports/utils/queries/insights-reports-queries';

import './insights-reports-queries-modal-step-details.scss';

import type {
  QueryFieldType,
  QueryKeyword,
  QueryModalErrors,
  QueryModalTextFields
} from 'modules/insights/reports/types';

applyTranslations(require('modules/insights/reports/components/queries/modals/insights-reports-queries-modal.i18n.json'));

type Props = {
  textFields: QueryModalTextFields,
  errors: QueryModalErrors,
  isShowingKpis: boolean,
  onTextFieldChange: (fieldType: QueryFieldType, value: string) => void,
  onHashtagsFieldChange: (value: Array<QueryKeyword>) => void,
  onShowKpisToggle: () => void
};

const MODAL_MSG_KEY = 'insights_reports_queries_modal';

class InsightsReportsQueriesModalStepDetails extends React.PureComponent<Props> {
  handleHashtagsChange = (values: Array<string>): void => {
    const { onHashtagsFieldChange } = this.props;

    onHashtagsFieldChange(values.map(value => ({
      keyword: value,
      type: HASHTAG
    })));
  };

  renderTooltip(type: 'hashtags'): React.ReactNode {
    return (
      <div className="insights-reports-queries-modal-step-search-terms__tooltip">
        <Tooltip
          message={__t(`${MODAL_MSG_KEY}.tooltips.${type}`)}
          width="long"
          showQuestionMark
        />
      </div>
    );
  }

  renderTextField(fieldType: string): React.ReactNode {
    const { textFields, errors, onTextFieldChange } = this.props;

    return (
      <div className="insights-reports-queries-modal-step-details__text-field">
        <InsightsReportsQueriesInputField
          key={`insights-reports-queries-modal-step-search-terms__text-field--${fieldType}}`}
          fieldType={fieldType}
          value={textFields[fieldType]}
          autoFocus={fieldType === TITLE}
          hasError={errors[fieldType]}
          onChange={onTextFieldChange}
        />
      </div>
    );
  }

  renderTitle(): React.ReactNode {
    return (
      <FormField
        direction="column"
        label={__t(`${MODAL_MSG_KEY}.labels.${TITLE}`)}
        labelType="highlighted"
        fontSize="small"
        showValidationIcon={false}
        showRequiredMark
      >
        {this.renderTextField('title')}
      </FormField>
    );
  }

  renderHashtags(): React.ReactNode {
    const { textFields, errors } = this.props;

    return (
      <FormField
        direction="column"
        label={__t(`${MODAL_MSG_KEY}.labels.${HASHTAGS}`)}
        labelType="highlighted"
        fontSize="small"
        tooltipMessage={__t(`${MODAL_MSG_KEY}.tooltips.${HASHTAGS}`)}
        className="insights-reports-queries-modal-step-details__form-field"
        showValidationIcon={false}
        showRequiredMark
      >
        <InputArray
          name={HASHTAGS}
          prefix="#"
          max={MAX_NUM_ITEMS[HASHTAGS]}
          value={textFields[HASHTAGS].map(item => item.keyword)}
          placeholder={__t(`${MODAL_MSG_KEY}.placeholders.${HASHTAGS}`, {
            count: MAX_NUM_ITEMS[HASHTAGS]
          })}
          hasError={errors[HASHTAGS]}
          sanitizeFunction={sanitizeHashtagValue}
          onChange={this.handleHashtagsChange}
        />
        <div className="insights-reports-queries-modal-step-details__char-count">
          <CharacterCount
            charCount={textFields[HASHTAGS].length}
            charLimit={MAX_NUM_ITEMS[HASHTAGS]}
            labelOverride={__t(`${MODAL_MSG_KEY}.labels.tags_remaining`)}
            align="right"
            size="normal"
            highlightOnError
          />
        </div>
      </FormField>
    );
  }

  renderKpis(): React.ReactNode {
    const { textFields, isShowingKpis, onTextFieldChange, onShowKpisToggle } = this.props;

    const kpisFieldsClasses = classnames('insights-reports-queries-modal-step-details__kpis-fields', {
      'insights-reports-queries-modal-step-details__kpis-fields--visible': isShowingKpis
    });

    return (
      <div className="insights-reports-queries-modal-step-details__kpis">
        <div
          className="insights-reports-queries-modal-step-details__kpis-link"
          onClick={onShowKpisToggle}
        >
          {__t(`${MODAL_MSG_KEY}.links.customize`)}
        </div>
        <div className={kpisFieldsClasses}>
          <InsightsReportsQueriesModalStepDetailsKpi
            fieldType={IMPRESSIONS}
            value={textFields[IMPRESSIONS]}
            placeholder={KPI_DEFAULTS[IMPRESSIONS]}
            onTextFieldChange={onTextFieldChange}
          />
          <InsightsReportsQueriesModalStepDetailsKpi
            fieldType={REACH}
            value={textFields[REACH]}
            placeholder={KPI_DEFAULTS[REACH]}
            onTextFieldChange={onTextFieldChange}
          />
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    return (
      <div className="insights-reports-queries-modal-step-details">
        {this.renderTitle()}
        {this.renderHashtags()}
        {this.renderKpis()}
      </div>
    );
  }
}

export default InsightsReportsQueriesModalStepDetails;
